import React, { useState } from "react";
import { DatePicker, Button, notification } from "antd";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import { useProjectState } from "../../../context/stateContext";
import "./index.css";
import { useEffect } from "react";

dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1,
});

const dateFormat = "YYYY/MM/DD";
const { RangePicker } = DatePicker;

const DatePickerComponent = ({
  limitationValue,
  limitationType,
  defaultThisMonth = false,
  onRangeChange = null,
}) => {
  const isLocal = !!defaultThisMonth;
  const [shortDateChanger, setShortDateChanger] = useState(1);
  // 1 === "Ден"
  // 2 === "Месец"
  const { startDate, setStartDate, endDate, setEndDate } = useProjectState();

  // Local state for component-only mode (when defaultThisMonth is true)
  const [localStartDate, setLocalStartDate] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [localEndDate, setLocalEndDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );

  // inform parent of initial local range when in local mode
  useEffect(() => {
    if (isLocal && onRangeChange) {
      onRangeChange(localStartDate, localEndDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDatePickerChange = (key, dates) => {
    const [selectedStartDate, selectedEndDate] = dates;
    if (limitationValue !== undefined && limitationType !== undefined) {
      if (
        dayjs(selectedEndDate).diff(
          dayjs(selectedStartDate),
          limitationType,
          true
        ) > limitationValue
      ) {
        notification["error"]({
          message: `Максималния времеви период не може да бъде повече от 1 ${
            limitationType === "month" ? "месец" : "ден"
          }.`,
        });
        return;
      }
    }

    if (isLocal) {
      setLocalStartDate(dayjs(selectedStartDate).format("YYYY-MM-DD"));
      setLocalEndDate(dayjs(selectedEndDate).format("YYYY-MM-DD"));
      if (onRangeChange)
        onRangeChange(
          dayjs(selectedStartDate).format("YYYY-MM-DD"),
          dayjs(selectedEndDate).format("YYYY-MM-DD")
        );
    } else {
      setStartDate(selectedStartDate);
      setEndDate(selectedEndDate);
      if (onRangeChange) onRangeChange(selectedStartDate, selectedEndDate);
    }
  };

  const validateDateRange = (selectedStartDate, selectedEndDate) => {
    if (shortDateChanger === 1) {
      if (isLocal) {
        setLocalStartDate(selectedStartDate);
        setLocalEndDate(selectedStartDate);
        if (onRangeChange) onRangeChange(selectedStartDate, selectedStartDate);
      } else {
        setStartDate(selectedStartDate);
        setEndDate(selectedStartDate);
        if (onRangeChange) onRangeChange(selectedStartDate, selectedStartDate);
      }
    } else {
      if (isLocal) {
        setLocalStartDate(selectedStartDate);
        setLocalEndDate(selectedEndDate);
        if (onRangeChange) onRangeChange(selectedStartDate, selectedEndDate);
      } else {
        setStartDate(selectedStartDate);
        setEndDate(selectedEndDate);
        if (onRangeChange) onRangeChange(selectedStartDate, selectedEndDate);
      }
    }
  };

  const incrementDate = () => {
    if (shortDateChanger === 1) {
      const base = isLocal ? localStartDate : startDate;
      const newStartDate = dayjs(base).add(1, "day").format("YYYY-MM-DD");
      validateDateRange(newStartDate);
    } else {
      const base = isLocal ? localStartDate : startDate;
      const selectedStartDate = dayjs(base)
        .add(1, "month")
        .startOf("month")
        .format("YYYY-MM-DD");
      const selectedEndDate = dayjs(base)
        .add(1, "month")
        .endOf("month")
        .format("YYYY-MM-DD");
      validateDateRange(selectedStartDate, selectedEndDate);
    }
  };

  const decrementDate = () => {
    if (shortDateChanger === 1) {
      const base = isLocal ? localStartDate : startDate;
      const newStartDate = dayjs(base).subtract(1, "day").format("YYYY-MM-DD");
      validateDateRange(newStartDate);
    } else {
      const base = isLocal ? localStartDate : startDate;
      const selectedStartDate = dayjs(base)
        .subtract(1, "month")
        .startOf("month")
        .format("YYYY-MM-DD");
      const selectedEndDate = dayjs(base)
        .subtract(1, "month")
        .endOf("month")
        .format("YYYY-MM-DD");
      validateDateRange(selectedStartDate, selectedEndDate);
    }
  };

  const onClickShortDateChanger = () => {
    if (shortDateChanger === 1) {
      if (isLocal) {
        const s = dayjs().startOf("month").format("YYYY-MM-DD");
        const e = dayjs().format("YYYY-MM-DD"); // 1st..today
        setLocalStartDate(s);
        setLocalEndDate(e);
        if (onRangeChange) onRangeChange(s, e);
      } else {
        setStartDate(dayjs().startOf("month").format("YYYY-MM-DD"));
        setEndDate(dayjs().endOf("month").format("YYYY-MM-DD"));
      }
      setShortDateChanger(2);
    } else {
      if (isLocal) {
        const today = dayjs().format("YYYY-MM-DD");
        setLocalStartDate(today);
        setLocalEndDate(today);
        if (onRangeChange) onRangeChange(today, today);
      } else {
        setStartDate(dayjs().format("YYYY-MM-DD"));
        setEndDate(dayjs().format("YYYY-MM-DD"));
      }
      setShortDateChanger(1);
    }
  };

  return (
    <>
      <RangePicker
        allowClear={false}
        value={[
          dayjs(isLocal ? localStartDate : startDate, { format: "YYYY-MM-DD" }),
          dayjs(isLocal ? localEndDate : endDate, { format: "YYYY-MM-DD" }),
        ]}
        format={dateFormat}
        presets={[
          { label: "Днес", value: [dayjs(), dayjs()] },
          {
            label: "Вчера",
            value: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
          },
          {
            label: "Тази седмица",
            value: [dayjs().startOf("week"), dayjs().endOf("week")],
          },
          {
            label: "Този месец",
            value: [dayjs().startOf("month"), dayjs().endOf("month")],
          },
          { label: "3 месеца", value: [dayjs().subtract(3, "month"), dayjs()] },
        ]}
        onChange={handleDatePickerChange}
        className="common-short-date-picker"
        inputReadOnly={true}
      />
      <div className="common-date-picker-short-date-picker">
        <Button onClick={decrementDate}>-</Button>

        <Button
          onClick={onClickShortDateChanger}
          disabled={limitationType === "day"}
          className="common-short-date-picker-day-month-button"
        >
          {shortDateChanger === 1 ? "Ден" : "Месец"}
        </Button>

        <Button
          onClick={incrementDate}
          disabled={
            shortDateChanger === 1
              ? dayjs(isLocal ? localStartDate : startDate).add(1, "day") >
                dayjs()
              : dayjs(isLocal ? localStartDate : startDate).add(1, "month") >
                dayjs().endOf("month")
          }
        >
          +
        </Button>
      </div>
    </>
  );
};

export default DatePickerComponent;
